import React, { useRef, useState } from "react";
import axios from "axios";
import slugify from "slugify";
import { baseAPI } from "../../config";
import { toast } from "react-toastify";
import { uploadFileToS3 } from "../../services/s3Service";

function UploadFiles({ expedient }) {
  const fileInput = useRef();
  const [docName, setDocName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const expedientId = expedient._id;
  const documents = expedient.documents || [];

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    // Validar el tamaño del archivo (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. El tamaño máximo es 50MB");
      return;
    }

    // Validar el tipo de archivo
    const allowedTypes = [
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
      'text/rtf',
      'text/plain',
      'text/markdown',
      // Spreadsheets
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/vnd.oasis.opendocument.spreadsheet',
      // Presentations
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.oasis.opendocument.presentation',
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/tiff',
      'image/heic',
      // Audio
      'audio/mpeg',
      'audio/mp4',
      'audio/wav',
      'audio/ogg',
      // Video
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/x-matroska',
      'video/webm',
      // Archives
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/x-tar',
      'application/gzip'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido. Solo se aceptan documentos, hojas de cálculo, presentaciones, imágenes, audio, video y archivos comprimidos");
      return;
    }

    setSelectedFile(file);
  };

  const getFileExtension = (filename) => {
    return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile || !docName) {
      toast.error("Por favor, selecciona un archivo y especifica un nombre");
      return;
    }

    setIsUploading(true);

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`
    };

    const extension = getFileExtension(selectedFile.name)
    const slug = slugify(docName, { lower: true, strict: true }) + extension

    const isDuplicate = documents.some(doc => doc.slug === slug);
    if (isDuplicate) {
      toast.error("El nombre del documento ya existe. Por favor, elige otro nombre.");
      setIsUploading(false);
      return;
    }

    try {
      // Upload file to S3
      const uploadResult = await uploadFileToS3(selectedFile, expedientId, slug, docName, selectedFile.type);
      
      // Update expedient with document info
      const docObject = {
        url: uploadResult.url,
        file_name: docName,
        file_type: selectedFile.type,
        slug: slug,
        extension,
      };

      await axios.post(`${baseAPI}/documents/add-to-expedient/${expedientId}`, docObject, { headers });
      
      toast.success("¡Documento subido correctamente!");
      window.location.reload();
    } catch (error) {
      console.error("Error al subir el documento:", error);
      toast.error("Error al subir el documento. Por favor, inténtalo de nuevo.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="col-span-full xl:col-span-6 bg-white shadow-lg rounded-lg border border-gray-200">
      <header className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">Subir Documento</h2>
      </header>
      
      <div className="p-5">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                htmlFor="file-input"
              >
                Archivo
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors duration-200 ${
                  isDragging
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.md,.xls,.xlsx,.csv,.ods,.ppt,.pptx,.odp,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.heic,.mp3,.m4a,.wav,.ogg,.mp4,.mov,.avi,.mkv,.webm,.zip,.rar,.7z,.tar,.gz"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  ref={fileInput}
                  onChange={handleFileSelect}
                  disabled={isUploading}
                />
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="mt-4 flex text-sm text-gray-600 text-center justify-center">
                    <label
                      htmlFor="file-input"
                      className="relative cursor-pointer rounded-lg font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
                    >
                      <span>Sube un archivo</span>
                    </label>
                    <p className="pl-1">o arrastra y suelta</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Documentos, hojas de cálculo, presentaciones, imágenes, audio, video y archivos comprimidos hasta 50MB
                  </p>
                </div>
                {selectedFile && (
                  <div className="mt-4 text-sm text-gray-600 text-center justify-center">
                    <p className="font-medium">Archivo seleccionado:</p>
                    <p className="truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                htmlFor="doc-name"
              >
                Nombre del documento
              </label>
              <input
                id="doc-name"
                type="text"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
                placeholder="Ej: contrato, préstamo, burofax..."
                onChange={(e) => setDocName(e.target.value)}
                disabled={isUploading}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              type="submit"
              disabled={isUploading || !selectedFile || !docName}
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Subiendo...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Subir documento
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UploadFiles;
